import { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Plus, RefreshCw, Search } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { AdminCityCreateRequest } from "@citybrews/shared/adminSchemas";
import { useAdminHeader } from "../AdminShell";
import { apiGet, apiSend } from "../api";
import { CityDetailPane, CityHeaderSummary, CityHeaderTabs } from "./CityPage";
import type { Stats } from "../types";
import { EmptyState, Field, PageError, pct, textOrNull, numberOrNull } from "../ui-helpers";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

const fetcher = <T,>(path: string) => apiGet<T>(path);
type StatsCity = Stats["cities"][number];
type CountryGroup = { country: string; countryCode: string; cities: StatsCity[] };

const citySchema = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  country: z.string().min(1),
  countryCode: z.string().min(2),
  cityCode: z.string().min(1),
  centerLat: z.coerce.number().nullable().optional(),
  centerLng: z.coerce.number().nullable().optional(),
  defaultZoom: z.coerce.number().nullable().optional(),
  timezone: z.string().min(1),
  flagEmoji: z.string().optional(),
  neighborhoods: z.string().optional(),
});
type CityInput = z.input<typeof citySchema>;

const placeSchema = z.object({
  name: z.string().min(1),
  category: z.string().min(1),
  neighborhood: z.string().optional(),
  address: z.string().optional(),
  website: z.string().optional(),
});
type PlaceInput = z.input<typeof placeSchema>;

export function CitiesPage() {
  const { slug } = useParams();
  const { data, error, isLoading } = useSWR<Stats>("/admin/api/stats", fetcher, { refreshInterval: 10000 });
  const [params, setParams] = useSearchParams();
  const cityQ = params.get("cityQ") ?? "";
  const citySearch = cityQ.trim().toLowerCase();
  const cities = useMemo(() => {
    const allCities = data?.cities ?? [];
    if (!citySearch) return allCities;
    return allCities.filter((city) => `${city.name} ${city.slug}`.toLowerCase().includes(citySearch));
  }, [citySearch, data?.cities]);
  const countryGroups = useMemo(() => {
    const groupsByCountry: Record<string, CountryGroup> = {};
    for (const city of cities) {
      const country = city.country.trim() || "Unknown";
      const countryCode = city.countryCode.trim().toUpperCase() || "UN";
      const groupKey = `${country.toLowerCase()}|${countryCode.toLowerCase()}`;
      groupsByCountry[groupKey] ??= { country, countryCode, cities: [] };
      groupsByCountry[groupKey].cities.push(city);
    }
    return Object.values(groupsByCountry).sort(sortCountryGroups).map((group) => ({
      ...group,
      cities: [...group.cities].sort(sortCities),
    }));
  }, [cities]);
  const selectedCity = data?.cities.find((city) => city.slug === slug);
  function setSearch(value: string) {
    const next = new URLSearchParams(params);
    value ? next.set("cityQ", value) : next.delete("cityQ");
    setParams(next);
  }
  function cityHref(citySlug: string) {
    return cityQ ? `/cities/${citySlug}?cityQ=${encodeURIComponent(cityQ)}` : `/cities/${citySlug}`;
  }
  useAdminHeader({
    title: "Cities",
    breadcrumbs: slug ? [{ label: "Cities", to: "/cities" }, { label: selectedCity?.name ?? slug }] : [{ label: "Cities" }],
    actions: (
      <>
        {slug && <Button variant="outline" onClick={() => apiSend(`/admin/api/cities/${slug}/republish`, "POST", { force: false }).then(() => toast.success("City republish queued")).catch((error) => toast.error(error instanceof Error ? error.message : "Republish failed"))}><RefreshCw /> Republish</Button>}
      </>
    ),
  }, [selectedCity?.name, slug]);
  if (error) return <PageError error={error} />;
  return (
    <div className="grid h-full grid-cols-[280px_minmax(0,1fr)] overflow-hidden">
      <aside className="flex min-h-0 flex-col border-r bg-card/30">
        <div className="flex h-20 shrink-0 flex-col justify-center gap-2 border-b px-3 py-2">
          <div className="flex h-6 items-center justify-between gap-2">
            <div className="min-w-0 truncate text-xs font-medium text-muted-foreground">{data ? `${data.cities.length.toLocaleString()} cities` : "Loading cities"}</div>
            <AddCityDialog />
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" value={cityQ} onChange={(event) => setSearch(event.target.value)} placeholder="Search cities" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {isLoading && Array.from({ length: 8 }, (_, index) => <Skeleton key={index} className="mb-2 h-12" />)}
          {!isLoading && countryGroups.length === 0 && <div className="px-2 py-6 text-center text-xs text-muted-foreground">No cities match</div>}
          {countryGroups.map((group) => (
            <div key={`${group.countryCode}-${group.country}`} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-2 text-xs font-semibold text-muted-foreground">
                <span className="truncate">{group.country}</span>
                <span className="font-mono text-[10px]">{group.countryCode}</span>
              </div>
              {group.cities.map((city) => {
                const selected = city.slug === slug;
                return (
                  <Link key={city.slug} to={cityHref(city.slug)} className={`mb-1 block rounded-md border px-3 py-2 text-sm transition-colors ${selected ? "border-border bg-background shadow-sm" : "border-transparent hover:bg-muted"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">{city.name}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">{city.published}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{city.places.toLocaleString()} places</span>
                      <span>{pct(city.published, city.survivors)}% live</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </aside>
      <section className="flex min-h-0 min-w-0 flex-col overflow-hidden">
        <div className="flex h-20 shrink-0 items-center justify-between gap-4 border-b bg-background px-5">
          {slug && (
            <>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                <CityHeaderSummary slug={slug} />
                <CityHeaderTabs />
              </div>
              <AddPlaceDialog slug={slug} />
            </>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {slug ? <CityDetailPane slug={slug} /> : <div className="grid h-full place-items-center p-6"><EmptyState title="Select a city" description="Choose a city from the list to review its pipeline, places, and metadata." /></div>}
        </div>
      </section>
    </div>
  );
}

function sortCountryGroups(a: CountryGroup, b: CountryGroup) {
  return a.country.localeCompare(b.country, undefined, { sensitivity: "base" }) || a.countryCode.localeCompare(b.countryCode, undefined, { sensitivity: "base" });
}

function sortCities(a: StatsCity, b: StatsCity) {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.slug.localeCompare(b.slug, undefined, { sensitivity: "base" });
}

function AddPlaceDialog({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const form = useForm<PlaceInput>({
    resolver: zodResolver(placeSchema),
    defaultValues: { name: "", category: "cafe", neighborhood: "", address: "", website: "" },
  });
  async function submit(raw: PlaceInput) {
    const values = placeSchema.parse(raw);
    const body = {
      name: values.name.trim(),
      category: values.category.trim(),
      neighborhood: textOrNull(values.neighborhood),
      address: textOrNull(values.address),
      website: textOrNull(values.website),
    };
    try {
      await apiSend(`/admin/api/cities/${slug}/places`, "POST", body);
      toast.success("Place created");
      setOpen(false);
      form.reset({ name: "", category: "cafe", neighborhood: "", address: "", website: "" });
      mutate((key) => typeof key === "string" && key.startsWith(`/admin/api/cities/${slug}/places`));
      mutate("/admin/api/stats");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Place create failed");
      console.info(`NEED-API: POST /admin/api/cities/${slug}/places — create a manually added place and tracked pipeline run`);
    }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" />}><Plus /> Add place</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Add place</DialogTitle></DialogHeader>
        <form className="grid gap-3" onSubmit={form.handleSubmit(submit)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input {...form.register("name")} /></Field>
            <Field label="Category"><Input {...form.register("category")} /></Field>
            <Field label="Neighborhood"><Input {...form.register("neighborhood")} /></Field>
            <Field label="Website"><Input {...form.register("website")} /></Field>
          </div>
          <Field label="Address"><Textarea {...form.register("address")} /></Field>
          <DialogFooter><Button type="submit">Create place</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddCityDialog() {
  const [open, setOpen] = useState(false);
  const form = useForm<CityInput>({
    resolver: zodResolver(citySchema),
    defaultValues: { name: "", slug: "", country: "", countryCode: "", cityCode: "", timezone: "", flagEmoji: "", neighborhoods: "" },
  });
  async function submit(raw: CityInput) {
    const values = citySchema.parse(raw);
    const body: AdminCityCreateRequest = {
      name: values.name.trim(),
      slug: textOrNull(values.slug) ?? undefined,
      country: values.country.trim(),
      countryCode: values.countryCode.trim().toLowerCase(),
      cityCode: values.cityCode.trim().toLowerCase(),
      centerLat: numberOrNull(values.centerLat),
      centerLng: numberOrNull(values.centerLng),
      defaultZoom: numberOrNull(values.defaultZoom),
      timezone: values.timezone.trim(),
      flagEmoji: textOrNull(values.flagEmoji),
      neighborhoods: (values.neighborhoods ?? "").split(",").flatMap((item) => {
        const value = item.trim();
        return value ? [value] : [];
      }),
    };
    try {
      await apiSend("/admin/api/cities", "POST", body);
      toast.success("City created");
      setOpen(false);
      mutate("/admin/api/stats");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "City create failed");
      console.info("NEED-API: POST /admin/api/cities — create city from typed admin form fields");
    }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button size="xs" variant="outline" />}><Plus /> Add city</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Add city</DialogTitle></DialogHeader>
        <form className="grid gap-3" onSubmit={form.handleSubmit(submit)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input {...form.register("name")} /></Field>
            <Field label="Slug"><Input {...form.register("slug")} /></Field>
            <Field label="Country"><Input {...form.register("country")} /></Field>
            <Field label="Country code"><Input {...form.register("countryCode")} /></Field>
            <Field label="City code"><Input {...form.register("cityCode")} /></Field>
            <Field label="Timezone"><Input {...form.register("timezone")} /></Field>
          </div>
          <Field label="Neighborhoods"><Textarea {...form.register("neighborhoods")} placeholder="Comma-separated" /></Field>
          <DialogFooter><Button type="submit">Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
